/**
 * O que o servidor aceita como imagem.
 *
 * O redimensionamento acontece no navegador: a foto de 5MB do iPhone vira ~40KB
 * de WebP antes de subir, o que poupa a rede do barbeiro e evita uma dependência
 * nativa no servidor. O preço é que o arquivo chega de um lugar em que não se
 * pode confiar — então tudo aqui parte do princípio de que os bytes podem ser
 * qualquer coisa, inclusive um JPEG de 8MB com nome de WebP.
 *
 * Nada aqui decodifica a imagem: lemos o cabeçalho, que é o suficiente para
 * saber o que ela é e de que tamanho. Decodificar bytes hostis é justamente o
 * que se quer evitar sem uma biblioteca dedicada.
 */

/** As duas coisas que a barbearia sobe: identidade visual e fotos. */
export type ImagePurpose = 'BRAND' | 'PHOTO';

export interface ImageLimits {
  maxBytes: number;
  maxDimension: number;
  /** Abaixo disto a imagem está pequena demais para servir a qualquer uso. */
  minDimension: number;
}

/**
 * Logo e ícone são pequenos e aparecem grandes; foto é grande e aparece
 * pequena. Os tetos vêm da conta do §8 da especificação: com ~60KB por foto,
 * uma barbearia gera menos de 200MB por ano.
 */
export const IMAGE_LIMITS: Record<ImagePurpose, ImageLimits> = {
  BRAND: { maxBytes: 256 * 1024, maxDimension: 1024, minDimension: 64 },
  PHOTO: { maxBytes: 400 * 1024, maxDimension: 1600, minDimension: 200 },
};

/** Quantas fotos cabem em um atendimento: antes, depois e dois ângulos. */
export const MAX_PHOTOS_PER_APPOINTMENT = 4;

export type ImageFormat = 'webp' | 'png' | 'jpeg';

export interface ImageProbe {
  format: ImageFormat;
  width: number;
  height: number;
}

function readU32LE(bytes: Uint8Array, offset: number): number {
  return (
    (bytes[offset] as number) |
    ((bytes[offset + 1] as number) << 8) |
    ((bytes[offset + 2] as number) << 16) |
    ((bytes[offset + 3] as number) << 24)
  );
}

function readU24LE(bytes: Uint8Array, offset: number): number {
  return (
    (bytes[offset] as number) |
    ((bytes[offset + 1] as number) << 8) |
    ((bytes[offset + 2] as number) << 16)
  );
}

function matches(bytes: Uint8Array, offset: number, signature: readonly number[]): boolean {
  if (bytes.length < offset + signature.length) return false;
  return signature.every((byte, index) => bytes[offset + index] === byte);
}

const RIFF = [0x52, 0x49, 0x46, 0x46] as const; // "RIFF"
const WEBP = [0x57, 0x45, 0x42, 0x50] as const; // "WEBP"
const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

/**
 * Dimensões de um WebP, lidas do cabeçalho do primeiro chunk.
 *
 * São três variantes com layouts diferentes, e o navegador escolhe qual gera
 * sem perguntar: `VP8 ` (com perda), `VP8L` (sem perda) e `VP8X` (estendido,
 * que é o que sai quando há transparência ou metadados).
 */
function probeWebp(bytes: Uint8Array): ImageProbe | null {
  if (!matches(bytes, 0, RIFF) || !matches(bytes, 8, WEBP)) return null;
  if (bytes.length < 30) return null;

  const chunk = String.fromCharCode(...bytes.slice(12, 16));

  if (chunk === 'VP8 ') {
    // Quadro-chave do VP8: assinatura de início de quadro e depois 14 bits de
    // largura e 14 de altura.
    if (!matches(bytes, 23, [0x9d, 0x01, 0x2a])) return null;
    return {
      format: 'webp',
      width: ((bytes[27] as number) | ((bytes[28] as number) << 8)) & 0x3fff,
      height: ((bytes[29] as number) | ((bytes[30] as number) << 8)) & 0x3fff,
    };
  }

  if (chunk === 'VP8L') {
    if (bytes[20] !== 0x2f) return null;
    const packed = readU32LE(bytes, 21);
    return {
      format: 'webp',
      width: (packed & 0x3fff) + 1,
      height: ((packed >> 14) & 0x3fff) + 1,
    };
  }

  if (chunk === 'VP8X') {
    return {
      format: 'webp',
      width: readU24LE(bytes, 24) + 1,
      height: readU24LE(bytes, 27) + 1,
    };
  }

  return null;
}

function probePng(bytes: Uint8Array): ImageProbe | null {
  if (!matches(bytes, 0, PNG) || bytes.length < 24) return null;
  // O IHDR é obrigatoriamente o primeiro chunk: largura e altura em big-endian.
  const width = (bytes[16] as number) * 0x1000000 + readBE24(bytes, 17);
  const height = (bytes[20] as number) * 0x1000000 + readBE24(bytes, 21);
  return { format: 'png', width, height };
}

function readBE24(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] as number) << 16) |
    ((bytes[offset + 1] as number) << 8) |
    (bytes[offset + 2] as number)
  );
}

/**
 * Dimensões de um JPEG: percorre os marcadores até achar um SOF.
 *
 * Aceitamos JPEG porque nem todo navegador antigo produz WebP no canvas — o
 * Safari só passou a fazer isso na versão 14. Sem esta porta, um iPhone velho
 * ficaria sem conseguir mandar foto nenhuma.
 */
function probeJpeg(bytes: Uint8Array): ImageProbe | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;

  let offset = 2;
  while (offset + 9 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }

    const marker = bytes[offset + 1] as number;
    // SOF0..SOF15, exceto os marcadores que não descrevem quadro (C4, C8, CC).
    const isFrameStart =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;

    if (isFrameStart) {
      return {
        format: 'jpeg',
        height: ((bytes[offset + 5] as number) << 8) | (bytes[offset + 6] as number),
        width: ((bytes[offset + 7] as number) << 8) | (bytes[offset + 8] as number),
      };
    }

    const length = ((bytes[offset + 2] as number) << 8) | (bytes[offset + 3] as number);
    if (length < 2) return null;
    offset += 2 + length;
  }

  return null;
}

/**
 * O que estes bytes são de verdade — pelo conteúdo, nunca pelo que o cliente
 * disse que eram.
 */
export function probeImage(bytes: Uint8Array): ImageProbe | null {
  return probeWebp(bytes) ?? probePng(bytes) ?? probeJpeg(bytes);
}

export const MIME_BY_FORMAT: Record<ImageFormat, string> = {
  webp: 'image/webp',
  png: 'image/png',
  jpeg: 'image/jpeg',
};

export type ImageRejection =
  | { reason: 'TOO_LARGE'; maxBytes: number }
  | { reason: 'UNKNOWN_FORMAT' }
  | { reason: 'TOO_SMALL'; minDimension: number }
  | { reason: 'TOO_WIDE'; maxDimension: number };

export type ImageCheck =
  | { ok: true; probe: ImageProbe; mimeType: string }
  | { ok: false; rejection: ImageRejection };

/** Mensagem em pt-BR da recusa. A API repassa; nenhuma tela inventa texto. */
export function describeRejection(rejection: ImageRejection): string {
  switch (rejection.reason) {
    case 'TOO_LARGE':
      return `Imagem grande demais (máximo ${Math.round(rejection.maxBytes / 1024)}KB depois de reduzida).`;
    case 'UNKNOWN_FORMAT':
      return 'Formato de imagem não reconhecido. Envie uma foto (JPEG, PNG ou WebP).';
    case 'TOO_SMALL':
      return `Imagem pequena demais (mínimo ${rejection.minDimension}px de lado).`;
    case 'TOO_WIDE':
      return `Imagem larga demais (máximo ${rejection.maxDimension}px de lado).`;
  }
}

/**
 * A porta de entrada de toda imagem do sistema.
 *
 * A ordem das checagens não é acidental: o tamanho em bytes vem primeiro porque
 * é a única verificação que não depende de olhar o conteúdo.
 */
export function checkImage(bytes: Uint8Array, purpose: ImagePurpose): ImageCheck {
  const limits = IMAGE_LIMITS[purpose];

  if (bytes.length > limits.maxBytes) {
    return { ok: false, rejection: { reason: 'TOO_LARGE', maxBytes: limits.maxBytes } };
  }

  const probe = probeImage(bytes);
  if (!probe) return { ok: false, rejection: { reason: 'UNKNOWN_FORMAT' } };

  const longest = Math.max(probe.width, probe.height);
  const shortest = Math.min(probe.width, probe.height);

  if (shortest < limits.minDimension) {
    return { ok: false, rejection: { reason: 'TOO_SMALL', minDimension: limits.minDimension } };
  }

  if (longest > limits.maxDimension) {
    return { ok: false, rejection: { reason: 'TOO_WIDE', maxDimension: limits.maxDimension } };
  }

  return { ok: true, probe, mimeType: MIME_BY_FORMAT[probe.format] };
}
